<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import { errorMessage, initials } from '@/api/format'
import { navigation } from '@/router/navigation'
import { useSessionStore } from '@/stores/session'

const session = useSessionStore()
const route = useRoute()
const mobileOpen = ref(false)
const tenantOpen = ref(false)
const tenantBusy = ref(false)
const tenantError = ref('')
const logoutBusy = ref(false)
const actionError = ref('')

const visibleNavigation = computed(() =>
  navigation
    .map((group) => ({ ...group, items: group.items.filter((item) => session.canAny(item.scopes)) }))
    .filter((group) => group.items.length > 0),
)

const currentLabel = computed(() => {
  const path = route.path
  return (
    visibleNavigation.value
      .flatMap((group) => group.items)
      .sort((left, right) => right.to.length - left.to.length)
      .find((item) => (item.to === '/' ? path === '/' : path.startsWith(item.to)))?.label ?? 'BlazeDSP'
  )
})

async function openTenants() {
  tenantOpen.value = true
  tenantBusy.value = true
  tenantError.value = ''
  try {
    await session.loadTenants()
  } catch (error) {
    tenantError.value = errorMessage(error)
  } finally {
    tenantBusy.value = false
  }
}

async function switchTenant(id: string) {
  if (id === session.session?.tenant_id) return
  tenantBusy.value = true
  tenantError.value = ''
  try {
    await session.switchTenant(id)
    tenantOpen.value = false
    window.location.reload()
  } catch (error) {
    tenantError.value = errorMessage(error)
  } finally {
    tenantBusy.value = false
  }
}

async function logout() {
  logoutBusy.value = true
  actionError.value = ''
  try {
    await session.logout()
  } catch (error) {
    actionError.value = errorMessage(error)
    logoutBusy.value = false
  }
}
</script>

<template>
  <div class="app-layout" :class="{ 'nav-open': mobileOpen }">
    <button
      class="nav-scrim"
      type="button"
      aria-label="Cerrar navegación"
      @click="mobileOpen = false"
    ></button>
    <aside class="sidebar" aria-label="Navegación principal">
      <div class="brand">
        <span class="brand-logo">DSP</span>
        <span><strong>BlazeDSP</strong><small>Service operations</small></span>
      </div>

      <button class="tenant-button" type="button" @click="openTenants">
        <span class="tenant-avatar">{{ initials(session.tenantName) }}</span>
        <span
          ><strong>{{ session.tenantName }}</strong
          ><small>{{ session.session?.tenant_id }}</small></span
        >
        <span aria-hidden="true">⌄</span>
      </button>

      <nav class="nav-groups">
        <section v-for="group in visibleNavigation" :key="group.label" class="nav-group">
          <h2>{{ group.label }}</h2>
          <RouterLink
            v-for="item in group.items"
            :key="item.to"
            :to="item.to"
            :class="{ active: item.to === '/' ? route.path === '/' : route.path.startsWith(item.to) }"
            @click="mobileOpen = false"
          >
            <span class="nav-icon" aria-hidden="true">{{ item.icon }}</span>
            {{ item.label }}
          </RouterLink>
        </section>
      </nav>

      <div class="operator">
        <span class="operator-avatar">{{ initials(session.operatorName) }}</span>
        <span
          ><strong>{{ session.operatorName }}</strong
          ><small>{{ session.session?.user?.email }}</small></span
        >
        <button type="button" :disabled="logoutBusy" aria-label="Cerrar sesión" @click="logout">↪</button>
      </div>
      <p v-if="actionError" class="sidebar-error" role="alert">{{ actionError }}</p>
    </aside>

    <section class="workspace">
      <header class="topbar">
        <button
          class="mobile-nav-button"
          type="button"
          aria-label="Abrir navegación"
          @click="mobileOpen = true"
        >
          ☰
        </button>
        <div>
          <span class="eyebrow">Centro de operaciones</span><strong>{{ currentLabel }}</strong>
        </div>
        <div class="live-status"><span></span> API real</div>
      </header>
      <main id="main-content" class="content"><RouterView /></main>
    </section>

    <Teleport to="body">
      <div v-if="tenantOpen" class="modal-backdrop" @click.self="tenantOpen = false">
        <section class="modal-card" role="dialog" aria-modal="true" aria-labelledby="tenant-title">
          <header class="modal-header">
            <div>
              <p class="eyebrow">Blaze Identity</p>
              <h2 id="tenant-title">Cambiar organización</h2>
            </div>
            <button class="icon-button" type="button" aria-label="Cerrar" @click="tenantOpen = false">
              ×
            </button>
          </header>
          <div v-if="tenantBusy" class="state-inline">Consultando memberships…</div>
          <p v-else-if="tenantError" class="alert error" role="alert">{{ tenantError }}</p>
          <div v-else class="tenant-list">
            <button
              v-for="tenant in session.tenants"
              :key="tenant.id"
              type="button"
              :disabled="tenant.id === session.session?.tenant_id"
              @click="switchTenant(tenant.id)"
            >
              <span class="tenant-avatar">{{ initials(tenant.name) }}</span>
              <span
                ><strong>{{ tenant.name }}</strong
                ><small>{{ tenant.role }}</small></span
              >
              <span>{{ tenant.id === session.session?.tenant_id ? 'Actual' : 'Cambiar' }}</span>
            </button>
            <p v-if="session.tenants.length === 0" class="muted">No hay otras organizaciones disponibles.</p>
          </div>
        </section>
      </div>
    </Teleport>
  </div>
</template>
